class MetalHouseNumberSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.input = this.querySelector('[data-number-input]');
    this.error = this.querySelector('[data-number-error]');
    this.calculation = this.querySelector('[data-number-calculation]');
    this.total = this.querySelector('[data-number-total]');
    this.keys = Array.from(this.querySelectorAll('[data-number-key]'));
    this.maximumDigits = Number(this.dataset.maximumDigits) || 8;
    this.propertyName = this.dataset.propertyName || 'House number';
    this.formId = this.dataset.productFormId || '';
    this.variantMap = this.#buildVariantMap();

    this.#createBreakdown();

    this.input?.addEventListener('input', this.#handleInput);
    this.input?.addEventListener('blur', this.#validate);
    this.querySelector('[data-number-backspace]')?.addEventListener('click', this.#removeLastDigit);
    this.querySelector('[data-number-clear]')?.addEventListener('click', this.#clear);

    for (const key of this.keys) {
      key.addEventListener('click', this.#appendDigit);
      const digit = key.dataset.numberKey || '';
      key.disabled = !this.variantMap[digit]?.available;
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#handleInput);
    this.input?.removeEventListener('blur', this.#validate);
    this.querySelector('[data-number-backspace]')?.removeEventListener('click', this.#removeLastDigit);
    this.querySelector('[data-number-clear]')?.removeEventListener('click', this.#clear);

    for (const key of this.keys || []) {
      key.removeEventListener('click', this.#appendDigit);
    }

    delete this.dataset.initialized;
  }

  getCartItems() {
    const number = this.input?.value || '';
    if (!this.#validate() || !number) return [];

    const setId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    return Array.from(number).map((digit, index) => ({
      variantId: String(this.variantMap[digit].id),
      quantity: 1,
      properties: {
        [this.propertyName]: number,
        _number_set: setId,
        _number_position: String(index + 1),
      },
    }));
  }

  focusInput() {
    this.input?.focus();
  }

  #createBreakdown() {
    if (!document.getElementById('mhn-number-breakdown-style')) {
      const style = document.createElement('style');
      style.id = 'mhn-number-breakdown-style';

      style.textContent = `
        metal-house-number-selector .mhn-number-breakdown {
          width: 100%;
          margin: 12px 0 0;
          border-collapse: collapse;
          background: transparent;
          color: inherit;
          font-family: inherit;
          font-size: 11px;
          line-height: 1.35;
          font-variant-numeric: tabular-nums;
        }

        metal-house-number-selector .mhn-number-breakdown[hidden] {
          display: none !important;
        }

        metal-house-number-selector .mhn-number-breakdown th,
        metal-house-number-selector .mhn-number-breakdown td {
          padding: 3px 0;
          border: 0;
          background: transparent;
          text-align: left;
        }

        metal-house-number-selector .mhn-number-breakdown th {
          padding-bottom: 5px;
          font-size: 10px;
          font-weight: 400;
          color: inherit;
          opacity: .55;
        }

        metal-house-number-selector .mhn-number-breakdown th:nth-child(2),
        metal-house-number-selector .mhn-number-breakdown td:nth-child(2) {
          text-align: center;
        }

        metal-house-number-selector .mhn-number-breakdown th:last-child,
        metal-house-number-selector .mhn-number-breakdown td:last-child {
          text-align: right;
        }

        metal-house-number-selector .mhn-number-breakdown tbody tr:last-child td {
          padding-bottom: 8px;
          border-bottom: 1px solid currentColor;
          border-bottom-color: color-mix(in srgb, currentColor 15%, transparent);
        }
      `;

      document.head.append(style);
    }

    this.breakdown = this.querySelector('[data-number-breakdown]');

    if (!this.breakdown) {
      this.breakdown = document.createElement('table');
      this.breakdown.className = 'mhn-number-breakdown';
      this.breakdown.setAttribute('data-number-breakdown', '');
      this.breakdown.setAttribute('aria-label', 'Selected numbers and quantities');
      this.breakdown.hidden = true;

      const head = this.breakdown.createTHead();
      const row = head.insertRow();

      for (const label of ['Number', 'Qty', 'Subtotal']) {
        const cell = document.createElement('th');
        cell.scope = 'col';
        cell.textContent = label;
        row.append(cell);
      }

      this.breakdown.createTBody();

      const summary = this.calculation?.parentElement;

      if (summary && this.contains(summary)) {
        summary.before(this.breakdown);
      } else {
        this.append(this.breakdown);
      }
    }

    this.breakdownBody = this.breakdown.tBodies[0];
  }

  #updateBreakdown(digits) {
    if (!this.breakdown || !this.breakdownBody) return;

    const counts = new Map();

    for (const digit of digits) {
      counts.set(digit, (counts.get(digit) || 0) + 1);
    }

    const rows = document.createDocumentFragment();
    const sortedCounts = [...counts.entries()].sort(
      ([a], [b]) => Number(a) - Number(b)
    );

    for (const [digit, quantity] of sortedCounts) {
      const row = document.createElement('tr');
      const variant = this.variantMap[digit];
      const subtotal = variant
        ? this.#formatMoney(variant.price * quantity)
        : '—';

      for (const value of [digit, String(quantity), subtotal]) {
        const cell = document.createElement('td');
        cell.textContent = value;
        row.append(cell);
      }

      rows.append(row);
    }

    this.breakdownBody.replaceChildren(rows);
    this.breakdown.hidden = digits.length === 0;
  }

  #buildVariantMap() {
    const dataElement = this.querySelector('[data-number-variants]');
    if (!dataElement?.textContent) return {};

    try {
      const data = JSON.parse(dataElement.textContent);
      const selectedOptions = Array.isArray(data.selectedOptions)
        ? data.selectedOptions
        : [];
      const variants = Array.isArray(data.variants) ? data.variants : [];
      const map = {};

      for (const variant of variants) {
        const options = Array.isArray(variant.options) ? variant.options : [];
        const digitIndex = options.findIndex(
          (option) => /^\d$/.test(String(option).trim())
        );

        if (digitIndex < 0) continue;

        const digit = String(options[digitIndex]).trim();

        const matchScore = options.reduce((score, option, index) => {
          if (index === digitIndex) return score;

          return score + (
            String(option) === String(selectedOptions[index]) ? 1 : 0
          );
        }, 0);

        if (!map[digit] || matchScore > map[digit].matchScore) {
          map[digit] = {
            id: variant.id,
            price: Number(variant.price) || 0,
            available: Boolean(variant.available),
            matchScore,
          };
        }
      }

      return map;
    } catch (error) {
      console.warn(
        '[metal-house-number-selector] Variant data could not be read.',
        error
      );

      return {};
    }
  }

  #handleInput = () => {
    if (!this.input) return;

    this.input.value = this.input.value
      .replace(/\D/g, '')
      .slice(0, this.maximumDigits);

    this.#sync();
  };

  #appendDigit = (event) => {
    if (!this.input || !(event.currentTarget instanceof HTMLElement)) return;

    if (this.input.value.length >= this.maximumDigits) {
      this.#showError(`You can enter up to ${this.maximumDigits} digits.`);
      return;
    }

    this.input.value += event.currentTarget.dataset.numberKey || '';
    this.#sync();
    this.input.focus();
  };

  #removeLastDigit = () => {
    if (!this.input) return;

    this.input.value = this.input.value.slice(0, -1);
    this.#sync();
    this.input.focus();
  };

  #clear = () => {
    if (!this.input) return;

    this.input.value = '';
    this.#sync();
    this.input.focus();
  };

  #sync() {
    const number = this.input?.value || '';
    const digits = Array.from(number);

    const unavailableDigit = digits.find(
      (digit) => !this.variantMap[digit]?.available
    );

    const totalCents = digits.reduce(
      (sum, digit) => sum + (this.variantMap[digit]?.price || 0),
      0
    );

    this.#updateBreakdown(digits);

    for (const key of this.keys || []) {
      const selected = number.includes(key.dataset.numberKey || '');

      key.classList.toggle('is-selected', selected);
      key.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    if (this.calculation && this.total) {
      if (!number) {
        this.calculation.textContent =
          'Enter your house number to calculate the total';
        this.total.textContent = '—';
      } else {
        this.calculation.textContent =
          `${digits.length} ${digits.length === 1 ? 'number' : 'numbers'}`;

        this.total.textContent =
          `${this.#formatMoney(totalCents)} total`;
      }
    }

    const quantitySelector = this.#getProductForm()?.querySelector(
      'quantity-selector-component'
    );

    if (quantitySelector?.setValue) {
      quantitySelector.setValue(String(Math.max(digits.length, 1)));
      quantitySelector.updateButtonStates?.();
    } else {
      const quantityInput = this.#getProductForm()?.querySelector(
        'input[name="quantity"]'
      );

      if (quantityInput) {
        quantityInput.value = String(Math.max(digits.length, 1));
      }
    }

    if (unavailableDigit) {
      this.#showError(`Number ${unavailableDigit} is currently unavailable.`);
    } else {
      this.#showError('');
      this.input?.setCustomValidity('');
    }
  }

  #validate = () => {
    const number = this.input?.value || '';
    let message = '';

    if (!number) {
      message = 'Enter at least one house number.';
    } else if (number.length > this.maximumDigits) {
      message = `You can enter up to ${this.maximumDigits} digits.`;
    } else {
      const missingDigit = Array.from(number).find(
        (digit) => !this.variantMap[digit]
      );

      const unavailableDigit = Array.from(number).find(
        (digit) => this.variantMap[digit] && !this.variantMap[digit].available
      );

      if (missingDigit) {
        message = `Number ${missingDigit} is not configured as a product variant.`;
      }

      if (unavailableDigit) {
        message = `Number ${unavailableDigit} is currently unavailable.`;
      }
    }

    this.input?.setCustomValidity(message);
    this.#showError(message);

    return !message;
  };

  #showError(message) {
    if (this.error) this.error.textContent = message;
  }

  #getProductForm() {
    if (!this.formId) return null;
    return document.getElementById(this.formId);
  }

  #formatMoney(cents) {
    const locale = this.dataset.locale || document.documentElement.lang || 'en';
    const currency = this.dataset.currency || 'USD';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (_error) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('metal-house-number-selector')) {
  customElements.define(
    'metal-house-number-selector',
    MetalHouseNumberSelector
  );
}